import {
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Spinner,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaTimes } from "react-icons/fa";
import { FiUsers } from "react-icons/fi";
import {
  OrganizationType,
  SourcingEventStatus,
  useGetOrganizationQuery,
  useGetSourcingEventQuery,
  useRemoveEmailInviteFromPrivateSourcingEventMutation,
  useRemoveOrganizationInviteFromPrivateSourcingEventMutation,
  type EmailInviteDto,
  type MinimalOrgDto,
  type OrganizationInvitePatchDto,
  type OrganizationInviteV2Dto,
  type SourcingEventV2Dto,
  type SourcingEventV2PatchDto,
} from "../../../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../../../common/auth/useLoggedInWithOrgContextState";
import { useEditor } from "../../../../../../common/editing/save-coordinator";
import { useApiError } from "../../../../../../common/errors/useApiError";
import { OrganizationSelector } from "../../../../../../common/organization/OrganizationSelector";
import { OrganizationTypeTag } from "../../../../../../common/organization/OrganizationTypeTag";
import { StepNavigation } from "../../../../../../common/StepNavigation";
import { useConfirmationModal } from "../../../../../../common/useConfirmationModal";
import { CreateOrganizationEntryModal } from "../../../../../organizations/entry/create-entry/CreateOrganizationEntryModal";
import { V2InviteOrganizationEntryModal } from "../../../../../v2/sourcing-event/V2InviteOrganizationEntryModal";
import { V2InviteOrganizationModal, type OrganizationInviteRecipients } from "./V2InviteOrganizationModal";
import { SectionCard } from "../../../../portal/view-single/SectionCard";

interface Props {
  eventId: string;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
}

export const InvitationsStep = ({ eventId, previousStep, nextStep, nextStepLabel }: Props) => {
  const { t } = useTranslation();
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const { trigger } = useApiError();
  const { state, updateField } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId);
  const { data: serverEvent } = useGetSourcingEventQuery({ eventId });
  const [removeEmailInvite, { isLoading: isRemovingEmailInvite }] =
    useRemoveEmailInviteFromPrivateSourcingEventMutation();
  const [removeOrgInviteFromPublishedEvent] = useRemoveOrganizationInviteFromPrivateSourcingEventMutation();
  const confirmationModal = useConfirmationModal();
  const isPublished = state.status === SourcingEventStatus.Published;

  const [selectedOrgEntry, setSelectedOrgEntry] = useState<MinimalOrgDto | undefined>();
  const [selectedOrgForRecipients, setSelectedOrgForRecipients] = useState<MinimalOrgDto | undefined>();
  const [showCreateEntryModal, setShowCreateEntryModal] = useState(false);

  const orgInvites = state.eventContext.type === "Private" ? state.eventContext.organizationInvites : [];
  const emailInvites: EmailInviteDto[] =
    serverEvent?.eventContext.type === "Private" ? serverEvent.eventContext.emailInvites ?? [] : [];

  const emailInvitesByEntry = useMemo(() => {
    const groups = new Map<string, EmailInviteDto[]>();
    emailInvites.forEach((invite) => {
      const entryId = invite.organizationEntry?.id ?? "";
      groups.set(entryId, [...(groups.get(entryId) ?? []), invite]);
    });
    return [...groups.values()];
  }, [emailInvites]);

  const invitedOrgIds = useMemo(() => orgInvites.map((invite) => invite.organizationId), [orgInvites]);
  const orgsToExclude = useMemo(
    () => [
      selectedOrg.id,
      ...invitedOrgIds,
      ...emailInvites.map((invite) => invite.organizationEntry?.id).filter((id): id is string => !!id),
    ],
    [selectedOrg.id, invitedOrgIds, emailInvites],
  );

  const toPatchInvite = (invite: OrganizationInviteV2Dto): OrganizationInvitePatchDto => ({
    organizationId: invite.organizationId,
    persons: (invite.persons ?? []).map((person) => ({ id: person.id })),
    emailInvites: (invite.emailInvites ?? []).map((email) => ({ email: email.email, language: email.language })),
  });

  const saveOrgInvites = (invites: OrganizationInvitePatchDto[]) => {
    updateField({ privateContext: { organizationInvites: invites } });
  };

  const addOrgInvite = (organization: MinimalOrgDto, recipients: OrganizationInviteRecipients) => {
    if (invitedOrgIds.includes(organization.id)) return;
    saveOrgInvites([
      ...orgInvites.map(toPatchInvite),
      {
        organizationId: organization.id,
        persons: recipients.personIds.map((id) => ({ id })),
        emailInvites: recipients.emailInvites,
      },
    ]);
  };

  const removeOrgInvite = async (organizationId: string) => {
    if (!isPublished) {
      saveOrgInvites(orgInvites.filter((invite) => invite.organizationId !== organizationId).map(toPatchInvite));
      return;
    }
    try {
      await removeOrgInviteFromPublishedEvent({ eventId, pathOrganizationId: organizationId }).unwrap();
    } catch (e) {
      trigger(e);
    }
  };

  const confirmRemoveOrgInvite = (organizationId: string) => {
    if (!isPublished) {
      removeOrgInvite(organizationId);
      return;
    }
    confirmationModal.show({
      title: t("Confirm removal"),
      description: t("The selected organization will immediately be removed from the event."),
      onConfirmed: () => removeOrgInvite(organizationId),
      confirmButtonColor: "red",
      confirmButtonText: t("Remove") ?? "",
    });
  };

  const selectOrganization = (organization?: MinimalOrgDto) => {
    if (!organization) return;
    if (organization.type === OrganizationType.Organization) {
      if (!invitedOrgIds.includes(organization.id)) setSelectedOrgForRecipients(organization);
    } else {
      setSelectedOrgEntry(organization);
    }
  };

  const handleRemoveEmailInvite = async (inviteId: string) => {
    try {
      await removeEmailInvite({ eventId, inviteId }).unwrap();
    } catch (e) {
      trigger(e);
    }
  };

  if (state.eventContext.type !== "Private") {
    return <Text pt="10">{t("Invitations apply to private procurements only.")}</Text>;
  }

  return (
    <Box pb="8" pt="10">
      {showCreateEntryModal && (
        <CreateOrganizationEntryModal
          onConfirm={async (newEntry) => {
            setSelectedOrgEntry({ type: OrganizationType.OrganizationEntry, ...newEntry });
          }}
          onSelectExisting={(org) => selectOrganization(org)}
          onClose={() => setShowCreateEntryModal(false)}
        />
      )}
      <V2InviteOrganizationEntryModal
        eventId={eventId}
        minimalOrgEntry={selectedOrgEntry}
        onClose={() => setSelectedOrgEntry(undefined)}
      />
      <V2InviteOrganizationModal
        minimalOrg={selectedOrgForRecipients}
        onClose={() => setSelectedOrgForRecipients(undefined)}
        onCreate={(recipients) => {
          if (selectedOrgForRecipients) addOrgInvite(selectedOrgForRecipients, recipients);
        }}
      />

      <SectionCard icon={FiUsers} title={t("Invited organizations")} count={orgInvites.length + emailInvites.length}>
        <Box pb="5">
          <Text color="smMuted" fontSize="sm" pb="2">
            {t("Invite organizations to this private procurement.")}
          </Text>
          <OrganizationSelector
            orgsToExclude={orgsToExclude}
            onChange={({ organization }) => selectOrganization(organization)}
          />
          <Text fontSize="sm" pt="1">
            {t("Please select an existing company or")}{" "}
            <Button
              variant="link"
              colorScheme="blue"
              fontWeight="bold"
              fontSize="sm"
              onClick={() => setShowCreateEntryModal(true)}
            >
              {t("create a new company entry")}
            </Button>
          </Text>
        </Box>
        <Box border="1px solid" borderColor="smBorder" rounded="md" overflow="hidden">
          <TableContainer>
            <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>{t("Organization name")}</Th>
                <Th>{t("Type")}</Th>
                <Th>{t("Recipients")}</Th>
                <Th textAlign="right">{t("Remove")}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {orgInvites.map((invite) => (
                <InvitedOrgRow
                  key={invite.organizationId}
                  invite={invite}
                  onRemove={() => confirmRemoveOrgInvite(invite.organizationId)}
                />
              ))}
              {emailInvitesByEntry.map((invites) => (
                <Tr key={invites[0].organizationEntry?.id ?? "no_org_entry"}>
                  <Td verticalAlign="top">
                    <Text fontSize="sm">{invites[0].organizationEntry?.name ?? t("No organization")}</Text>
                  </Td>
                  <Td verticalAlign="top">
                    <OrganizationTypeTag type={OrganizationType.OrganizationEntry} />
                  </Td>
                  <Td verticalAlign="top">
                    {invites.map((invite) => (
                      <Text key={invite.id} fontSize="sm" pb="1">
                        {invite.email}
                      </Text>
                    ))}
                  </Td>
                  <Td verticalAlign="top" textAlign="right">
                    {invites.map((invite) => (
                      <IconButton
                        key={invite.id}
                        aria-label={t("Remove") ?? "Remove"}
                        icon={<Icon as={FaTimes} />}
                        size="xs"
                        variant="ghost"
                        display="block"
                        ml="auto"
                        isDisabled={isRemovingEmailInvite}
                        onClick={() => handleRemoveEmailInvite(invite.id)}
                      />
                    ))}
                  </Td>
                </Tr>
              ))}
              {orgInvites.length + emailInvites.length === 0 && (
                <Tr>
                  <Td colSpan={4} textAlign="center" color="smMuted">
                    {t("No invites have been added")}
                  </Td>
                </Tr>
              )}
            </Tbody>
            </Table>
          </TableContainer>
        </Box>
      </SectionCard>

      <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={nextStepLabel} />
      {confirmationModal.modal}
    </Box>
  );
};

const InvitedOrgRow = ({ invite, onRemove }: { invite: OrganizationInviteV2Dto; onRemove: () => void }) => {
  const { t } = useTranslation();
  const { data: org, isLoading } = useGetOrganizationQuery({ orgId: invite.organizationId });

  return (
    <Tr>
      <Td verticalAlign="top">
        {isLoading ? (
          <Spinner size="xs" color="smPrimary" />
        ) : (
          <>
            <Text fontSize="sm">{org?.name ?? "–"}</Text>
            <Text fontSize="xs" color="smMuted">
              {org?.organizationNumber ?? "–"}
            </Text>
          </>
        )}
      </Td>
      <Td verticalAlign="top">
        <OrganizationTypeTag type={OrganizationType.Organization} />
      </Td>
      <Td verticalAlign="top">
        {(invite.persons ?? []).map((person) => (
          <Text key={person.id} fontSize="sm">
            {`${person.firstName} ${person.lastName} (${person.email})`.trim()}
          </Text>
        ))}
        {(invite.emailInvites ?? []).map((email) => (
          <Text key={email.email} fontSize="sm" pt="1">
            {email.email} <Tag size="sm">{t("Not registered")}</Tag>
          </Text>
        ))}
        {!invite.persons?.length && !invite.emailInvites?.length && (
          <Text fontSize="sm" color="smMuted">
            –
          </Text>
        )}
      </Td>
      <Td verticalAlign="top" textAlign="right">
        <IconButton
          aria-label={t("Remove") ?? "Remove"}
          icon={<Icon as={FaTimes} />}
          size="sm"
          variant="ghost"
          onClick={onRemove}
        />
      </Td>
    </Tr>
  );
};
